/**
 * Every string the family sees. Not i18n — just one place to edit the voice.
 *
 * Rules:
 *  - Sentence case. Active voice. Say what the button does.
 *  - An action keeps its name through the whole flow:
 *    "Lägg till uppgift" -> toast "Uppgift tillagd".
 *  - Empty states are invitations, not apologies.
 *  - Errors say what happened and what to do. They never say "Oj".
 *  - Dates/times/numbers never live here — format them with Intl.
 *
 * A few entries are **functions**, which is new as of the recurrence slice.
 * "Varje vecka på tisdagar" is a sentence with an Intl-formatted weekday in the
 * middle of it, and the alternatives were both worse: assembling it in
 * `format.ts` puts user-facing text outside this file, and splitting it into
 * two fragments makes the word order untranslatable and unreadable here. The
 * rule is unchanged — the *string* lives here, the *date* is still formatted by
 * Intl and handed in.
 */
/**
 * Swedish ordinal suffix, which is grammar rather than formatting — Intl has no
 * `sv` ordinal rules to ask, so it lives here with the words it attaches to.
 *
 * `:a` for första, andra, tjugoförsta, tjugoandra, trettioförsta; `:e` for
 * everything else. Eleventh and twelfth are elfte and tolfte, so they take `:e`
 * despite ending in 1 and 2 — the same exception English makes for 11th/12th.
 *
 * Only ever applied to a day of the month, so the range is 1–31.
 */
const ordinal = (n: number) =>
  (n % 10 === 1 || n % 10 === 2) && n !== 11 && n !== 12 ? ":a" : ":e";

export const copy = {
  app: {
    name: "Tessera",
    tabs: {
      today: "Idag",
      tasks: "Uppgifter",
      calendar: "Kalender",
      people: "Personer",
      parking: "Parkering",
    },
  },

  tasks: {
    title: "Uppgifter",
    add: "Lägg till uppgift",
    added: "Uppgift tillagd",
    placeholder: "Vad behöver göras?",
    assignTo: "Tilldela",
    unassigned: "Vem som helst",
    complete: "Markera som klar",
    completed: "Klar",
    undo: "Ångra",
    remove: "Ta bort uppgift",
    open: "Öppna uppgift",
    detail: "Uppgift",
    /** Renaming from detail. The action keeps this name through the flow. */
    rename: "Byt namn",
    renamePlaceholder: "Vad behöver göras?",
    // The word only. The date itself comes from format.ts.
    due: "Förfaller",
    noDue: "Inget datum",
    setDue: "Välj datum",
    clearDue: "Ta bort datum",
    overdue: "Försenad",
    removeConfirm: {
      title: "Ta bort uppgiften?",
      body: "Den försvinner för alla i hushållet. Det går inte att ångra.",
    },
    /**
     * The inline add row at the foot of every list — its placeholder, and the
     * text button that appears once something is typed. Deliberately shorter
     * than the floating action's "Lägg till uppgift": the list it sits in has
     * already said where it goes.
     */
    quickAdd: "Lägg till",
    /** The add row's accessible name, since its visible text is a placeholder. */
    quickAddTo: (list: string) => `Lägg till i ${list}`,
    /** Which list a task is in, on the detail screen. */
    list: "Lista",
    empty: {
      title: "Inget på listan",
      body: "Lägg till det första så dyker det upp i allas telefoner.",
    },
    emptyToday: { title: "Inget att göra idag", body: "Njut av det." },
  },

  lists: {
    add: "Ny lista",
    added: "Lista tillagd",
    namePlaceholder: "Vad heter listan?",
    rename: "Byt namn",
    manage: "Hantera listan",
    target: "Lägg i lista",
    /**
     * Functions, because four collapsed groups previously announced as four
     * identical "Dölj listan" — the button's `aria-label` overrides the `<h2>`
     * inside it, so the name of the list was the one thing it did not say.
     */
    expand: (name: string) => `Visa ${name}`,
    collapse: (name: string) => `Dölj ${name}`,
    remove: "Ta bort lista",
    removeConfirm: {
      title: "Ta bort listan?",
      // Says exactly what happens to the tasks, because nothing is deleted.
      // The destination is named by the caller — it is whichever list is first.
      body: (destination: string) =>
        `Uppgifterna flyttas till ${destination}. Ingen uppgift tas bort.`,
    },
    /** The last list cannot go: with no list there is nowhere to put a task. */
    removeLast: "Det här är den enda listan. Skapa en till först.",
    empty: {
      title: "Inget här än",
      body: "Lägg till något så syns det här.",
    },
  },

  calendar: {
    /**
     * Not the calendar screen's own title any more — that is the month being
     * looked at, formatted by Intl. This is the word for the place, used by
     * the back link and the not-found action on the event detail screen.
     */
    title: "Kalender",
    add: "Lägg till händelse",
    /** The header's chevrons. A month is changed by tapping, never by swipe. */
    prevMonth: "Föregående månad",
    nextMonth: "Nästa månad",
    /**
     * The selected day, with nothing on it.
     *
     * Not an empty state — the month around it may be full — so it is one
     * quiet line rather than an invitation. The docked action is the
     * invitation, and it is on screen either way.
     */
    dayEmpty: "Inget den här dagen.",
    /**
     * A grid cell that has a dot, read aloud. The dot is the whole of what a
     * sighted reader gets, so it is the whole of what this has to say; the
     * date itself is Intl-formatted and handed in, per the note at the top.
     *
     * Deliberately not a count. One dot per day with anything on it is the
     * decision the grid is built on, and a screen reader hearing "3 händelser"
     * where the screen shows one dot is that decision leaking back in.
     */
    dayWithEvents: (date: string) => `${date}, har något inplanerat`,
    added: "Händelse tillagd",
    allDay: "Heldag",
    repeats: "Upprepas",
    noRepeat: "Upprepas inte",
    editOne: "Bara den här händelsen",
    editAll: "Den här och alla kommande händelser",
    /**
     * The four presets, as they read on the picker and on the detail row —
     * one set of strings, so the row always says back exactly what was picked.
     * The weekday and the date come from `format.ts`; see the note at the top
     * of this file for why these are functions.
     */
    repeat: {
      title: "Upprepas",
      never: "Upprepas inte",
      daily: "Varje dag",
      weekly: (day: string) => `Varje vecka på ${day}ar`,
      /**
       * Takes the day twice: the Intl-formatted string to show, and the number
       * the suffix is decided from. Formatting stays in `format.ts` and grammar
       * stays here — reading the digit back off the string is what breaks in a
       * locale that does not use Latin digits.
       */
      monthly: (day: string, n: number) => `Varje månad den ${day}${ordinal(n)}`,
      yearly: (date: string) => `Varje år den ${date}`,
      /**
       * Shown whenever the event starts past the 28th, before anything is
       * picked — a household that puts something on the 31st deserves to know
       * before it silently misses February, not after. The months without that
       * date are skipped rather than slid to the 28th, which is RFC 5545's rule
       * and not ours.
       *
       * It names "Varje månad" because it sits under all four options and is
       * untrue of the other three.
       */
      monthlySkips: "”Varje månad” hoppar över månader utan det datumet.",
    },
    /** The detail screen's own title, before the event has loaded. */
    detail: "Händelse",
    placeholder: "Vad händer?",
    rename: "Byt namn",
    renamePlaceholder: "Vad händer?",
    /** Labels on the detail screen's when-rows. */
    starts: "Börjar",
    ends: "Slutar",
    noEnd: "Inget slut",
    setEnd: "Lägg till sluttid",
    clearEnd: "Ta bort sluttid",
    /** The whole day, rather than a time on it. */
    onDate: "Datum",
    remove: "Ta bort händelse",
    removeConfirm: {
      title: "Ta bort händelsen?",
      body: "Den försvinner för alla i hushållet. Det går inte att ångra.",
    },
    /**
     * A series is all-or-nothing, so the button says so **before** the confirm
     * does. The action keeps this name through the whole flow — the screen's
     * button and the confirm's button are the same words, which is the rule the
     * task flow currently breaks and this one must not.
     */
    removeSeries: "Ta bort alla tillfällen",
    removeSeriesConfirm: {
      title: "Ta bort alla tillfällen?",
      body: "Hela serien försvinner för alla i hushållet, inte bara den här dagen. Det går inte att ångra.",
    },
    empty: {
      title: "Inga händelser än",
      /**
       * Said "eller prenumerera på kalendern från telefonens kalenderapp"
       * until the calendar was real enough to show this to anyone. The ICS
       * feed is in Later and may be a long way off — an empty state is the
       * first thing a household sees, and it should not open by describing a
       * feature that does not exist.
       */
      body: "Lägg till det som gäller alla — träningar, födelsedagar, möten.",
      /**
       * No `action`. The month grid means the empty state is never alone on
       * the screen — the docked `add` is under it, in accent, saying the same
       * words — and "one primary action per screen" is not a rule two
       * identical buttons can both satisfy. `add` above is the one.
       */
    },
  },

  /**
   * Servicedagar — Solna's biweekly street cleaning. The screen lists the
   * streets the household parks on and which one the car is on now.
   *
   * The weekday, the window and the next date all come from `format.ts`; the
   * parity words and the sentence templates are the only strings here. Same
   * rule as everywhere: the *date* is Intl's, the *words around it* are ours.
   */
  parking: {
    title: "Servicedagar",
    add: "Lägg till gata",
    editTitle: "Ändra gata",
    namePlaceholder: "Gatans namn",
    empty: {
      title: "Inga gator än",
      body: "Lägg till en gata du parkerar på, så påminner appen dig innan servicedagen.",
    },
    weekdayLabel: "Veckodag",
    weekLabel: "Vecka",
    /**
     * The current ISO week and its parity, at the top of the screen — it is what
     * says whether the odd- or even-week streets have servicedag right now. The
     * number is Intl-formatted and handed in; the words are ours.
     */
    currentWeek: (week: string, parity: "odd" | "even") =>
      `Vecka ${week} · ${parity === "odd" ? "udda" : "jämna"} vecka`,
    fromLabel: "Från",
    toLabel: "Till",
    /** The two options in the week picker, and the words the row reads back. */
    parity: { odd: "Udda veckor", even: "Jämna veckor" },
    /** Lowercase, for the mid-line schedule summary. */
    parityShort: { odd: "udda veckor", even: "jämna veckor" },
    /**
     * The schedule under a street's name. The day is Intl's ("torsdag"),
     * pluralised here the same way the weekly-recurrence line is; the window is
     * Intl's too. e.g. "Torsdagar, udda veckor · 10:00–14:00".
     */
    summary: (day: string, parity: string, window: string) =>
      `${day}ar, ${parity} · ${window}`,
    /** The next occurrence line. "Nästa: tors 27 aug. kl. 10:00". */
    next: (day: string, time: string) => `Nästa: ${day} kl. ${time}`,
    /** The next occurrence when it falls in the current week — highlighted. */
    nextThisWeek: (day: string, time: string) => `Denna vecka: ${day} kl. ${time}`,
    /** The park toggle, by state. The street name is the household's own word. */
    park: (street: string) => `Parkera bilen på ${street}`,
    parkedAria: (street: string) => `Bilen är parkerad på ${street}. Avmarkera.`,
    parkedHere: "Bilen står här",
    manage: (street: string) => `Hantera ${street}`,
    /** The header button that asks the car where it is. */
    refresh: "Uppdatera bilens plats",
    /** The line under the header after a manual refresh. */
    status: {
      detecting: "Hämtar bilens plats…",
      parkedOn: (street: string) => `Bilen står på ${street}`,
      notFound: "Hittade inte bilen på någon gata i närheten.",
      unconfigured: "Bilens plats är inte inställd än.",
      error: "Kunde inte hämta bilens plats.",
    },
    edit: "Ändra gata",
    remove: "Ta bort gata",
    removeConfirm: {
      title: "Ta bort gatan?",
      body: (name: string) =>
        `${name} tas bort för alla i hushållet. Det går inte att ångra.`,
    },
  },

  people: {
    title: "Personer",
    invite: "Bjud in någon",
    /** The sheet the invite opens. Same name as the action that opened it. */
    inviteSheet: {
      title: "Bjud in någon",
      body: "Koden gäller i ett dygn och kan användas en gång.",
      copyLink: "Kopiera länk",
      copied: "Länk kopierad",
      failed: "Det gick inte att skapa en kod. Försök igen.",
    },
    /** Marks a person in the household who has no login of their own. */
    noAccount: "Inget konto",
    /** Same words as every other rename in the app. */
    rename: "Byt namn",
    renamePlaceholder: "Vad heter personen?",
    signOut: "Logga ut",
    /**
     * The outstanding-invites section under the roster. Hidden entirely when
     * there are none — an empty state for a feature this quiet would be more
     * furniture than the gap it closes.
     */
    invites: {
      title: "Väntande koder",
      remove: "Ta bort koden",
      removeConfirm: {
        title: "Ta bort koden?",
        body: "Den slutar fungera direkt. Det går inte att ångra.",
      },
      createdBy: (name: string) => `Av ${name}`,
      expires: (relative: string) => `Går ut ${relative}`,
    },
    empty: {
      title: "Bara du så här långt",
      body: "Bjud in hushållet så delar ni listor och datum.",
    },
    /**
     * Push reminders, toggled per device — this phone or laptop, not the whole
     * household. Sits by "Logga ut" because that is the other account-level
     * control, and like it, it is quiet: the screen's one primary action is the
     * invite.
     */
    push: {
      title: "Påminnelser",
      /**
       * Deliberately vague on the lead time. The 30-minute figure is a constant
       * that may move, and copy that names it goes stale without anyone noticing.
       */
      description: "Få en notis strax innan något i kalendern börjar.",
      enable: "Aktivera på den här enheten",
      disable: "Stäng av på den här enheten",
      failed: "Det gick inte att ändra. Försök igen.",
      /**
       * iOS enables Web Push only for apps on the home screen. Safari enforces
       * it, not us — so the toggle explains rather than failing silently.
       */
      iosHint: "Lägg till appen på hemskärmen för att få notiser här.",
      /** Permission was denied; script cannot ask again, only settings can. */
      blocked: "Notiser är avstängda i webbläsarens inställningar.",
      unsupported: "Den här webbläsaren stöder inte notiser.",
    },
  },

  /**
   * Signing in. Email and a one-time code — no password to keep track of for
   * four people, and no redirect out to a browser tab, which is what breaks a
   * PWA that was opened from the home screen.
   */
  auth: {
    title: "Tessera",
    intro: "Logga in med din e-post. Du får en engångskod.",
    email: "E-post",
    emailPlaceholder: "din@epost.se",
    sendCode: "Skicka kod",
    /**
     * The name step, which only a new account sees. It sits between the email
     * and the code because the name has to exist before the account does —
     * afterwards the member row has already been written.
     */
    nameTitle: "Vad ska vi kalla dig?",
    nameIntro: "Namnet som resten av familjen ser.",
    name: "Namn",
    namePlaceholder: "Ditt förnamn",
    codeTitle: "Skriv in koden",
    /** Followed by the address itself, which is data and not copy. */
    codeSentTo: "Vi skickade en kod till",
    code: "Engångskod",
    codePlaceholder: "123456",
    signIn: "Logga in",
    resend: "Skicka en ny kod",
    changeEmail: "Byt e-post",
    errors: {
      email: "Kontrollera e-postadressen.",
      code: "Koden stämmer inte. Försök igen.",
      expired: "Koden har gått ut. Be om en ny.",
      tooMany: "För många försök. Vänta en stund och försök igen.",
      generic: "Det gick inte just nu. Försök igen.",
    },
  },

  /** Signed in, but not in a household yet. */
  join: {
    title: "Gå med i hushållet",
    body: "Be någon som redan är med om en kod och skriv in den här.",
    /** Arrived from an invite link, so the code is already in the field. */
    fromLink: "Vi använder koden från länken. Du behöver inte skriva något.",
    code: "Inbjudningskod",
    codePlaceholder: "ABC123",
    action: "Gå med",
    invalid: "Koden gäller inte. Be om en ny.",
  },

  states: {
    loading: "Laddar",
    offline: "Du är offline. Ändringar sparas och synkas när du är uppkopplad igen.",
    syncing: "Synkar",
    saveFailed: "Det sparades inte. Kontrollera anslutningen och försök igen.",
    retry: "Försök igen",
    notFound: "Det här finns inte längre. Någon kan ha tagit bort det.",
  },

  actions: {
    save: "Spara",
    cancel: "Avbryt",
    delete: "Ta bort",
    confirmDelete: "Ta bort för alla",
  },
} as const;
